import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useSettingQuery } from '@/features/auth'
import { RECORDING_MIN_S } from '@/features/yakureki/recording/audioPolicy'
import type {
  RecordingEngineState,
  RecordingStartParams,
} from '@/features/yakureki/recording/types'
import { useRecordingEngine } from '@/features/yakureki/recording/useRecordingEngine'
import { useToast } from '@/shared/contexts/ToastContext'
import type { YakurekiDetail } from '@/shared/types/yakurekiCore'
import { removeYakurekiFromLocalCache } from '@/shared/apis/yakurekiSync'
import { executeSuperReload } from '@/shared/helpers/superReload'

import { deriveViewStatus } from './deriveViewStatus'
import { yakurekiPollKey } from './usePollEvent'
import type { ChecklistStep, RecordingViewModel, ViewStatus } from './types'

/**
 * 転写経路の解決 (log/207 PR-FRONT ①)。
 * custom_setting.transcription_mode が 'batch' の時だけ batch。未取得 (query loading /
 * 失敗) は従来挙動の streaming に倒す (= 確認できない状態で誤って batch を始めない)。
 * D の Switch (soap-settings) は同じ query を invalidate するため、toggle → 次の録音で
 * 新しい経路が使われる。
 */
export const resolveTranscriptionRoute = (
  transcriptionMode: 'batch' | 'streaming' | undefined
): 'streaming' | 'batch' =>
  transcriptionMode === 'batch' ? 'batch' : 'streaming'

// 録音開始時の経路。3 段で決まる: 呼び出し側の指定 → フリー録音 → ユーザー設定。
// 呼び出し側が指定するのは「その画面が経路に依存している」場合だけ (= 録音中に文字起こしが
// 出ることを前提にした画面。管理栄養士の記録)。部品は誰が指定したかを知らない。
// フリー録音が streaming 固定なのは log/192 §3.4 (batch は event auto-create を経ない)。
export const resolveStartRoute = ({
  requested,
  isFreeRecording,
  transcriptionMode,
}: {
  requested?: 'streaming' | 'batch'
  isFreeRecording?: boolean
  transcriptionMode: 'batch' | 'streaming' | undefined
}): 'streaming' | 'batch' =>
  requested ??
  (isFreeRecording ? 'streaming' : resolveTranscriptionRoute(transcriptionMode))

// 各 step の min dwell ms. base 1500 ± 500 = 1000-2000ms の jitter で機械的になりすぎず、
// それでいて step ごとの読み取り時間を担保する.
const STEP_DWELL_BASE_MS = 1500
const STEP_DWELL_JITTER_MS = 500
// 意図した batch 経路 (log/207 E): 工程が実時間で長い (数分オーダー) ため streaming の
// 定数では checklist が序盤で天井 (raw step) に張り付いて動かなくなる. 2.5 倍を初期値に
// 置き、 最終値は staging gate で #1272 の新しい raw 遷移 (step 2 が生きる) の上で決める.
const STEP_DWELL_BASE_BATCH_MS = 3750
const STEP_DWELL_JITTER_BATCH_MS = 1250
const sampleStepDwellMs = (isIntendedBatch: boolean): number =>
  isIntendedBatch
    ? STEP_DWELL_BASE_BATCH_MS +
      (Math.random() * 2 - 1) * STEP_DWELL_JITTER_BATCH_MS
    : STEP_DWELL_BASE_MS + (Math.random() * 2 - 1) * STEP_DWELL_JITTER_MS

interface UseRecordingStatusArgs {
  /**
   * polling 対象 event. L1 polling (usePollEvent) は caller 側 page が 1 instance だけ
   * 持ち、 ここでは **生 event を受け取る** 流儀にする (= L3 hook が L1 query を内包すると
   * 二重 polling になるため). 子 component も自前 query を張らず recordingStatus.event を読む.
   */
  event: YakurekiDetail | undefined
  /**
   * fallback 経路で stop された時に hook 内から呼ばれる commit. caller が legacy batch endpoint
   * (= PATCH /yakureki/{id}) に流す.
   */
  onFallbackCommit?: (blob: Blob) => Promise<void>
  /**
   * 「直前に完了した録音が意図した batch だった」 hint (history state 由来. 任意).
   * まとめて録音 → /soap 遷移のように、 録音した engine instance と生成中 checklist を
   * 表示する instance が別 page になる経路では engine.isIntendedBatch が引き継がれない
   * ため、 caller が invalidation 済みの hint (index.tsx の routedIntendedBatch) を渡して
   * dwell の経路分岐を成立させる. 未指定は false 扱い.
   */
  intendedBatchHint?: boolean
  /**
   * 「別 page の録音完了 (finalize ack) から遷移してきた」 hint (history state 由来. 任意).
   * 遷移先の engine は別 instance で idle、 cache はサーバが processing を書く前の
   * 'recording' のことがある. 証拠が無いと derive はそれを idle (mic) と読むので (ADR-0041)、
   * 新規録音 / まとめて録音 → /soap 遷移では caller が渡す. 寿命は intendedBatchHint と同じ
   * (この page で新しい録音が始まったら失効). 未指定は false 扱い.
   */
  finalizedHint?: boolean
}

/**
 * L1 (event) + L2 (engine) を合成して RecordingViewModel を返す compose hook.
 *
 * 責務:
 * - useRecordingEngine (L2) instance を内部で持つ
 * - 渡された event (L1) と engine を deriveViewStatus に渡して view-model を組み立てる
 * - transition guard (= flicker 防止) を施す:
 *   一度 status='completed' に到達した event は generating に巻き戻さない 1-bit ratchet.
 *   event id 切替で ratchet reset.
 *
 * 利用:
 *   const event = usePollEvent({yakurekiId}).data
 *   const recordingStatus = useRecordingStatus({event, onFallbackCommit})
 *   useActiveRecordingEmit(recordingStatus)
 *   <MeetingBlock recordingStatus={recordingStatus} />
 */
export const useRecordingStatus = (
  args: UseRecordingStatusArgs
): RecordingViewModel => {
  const { event, onFallbackCommit, intendedBatchHint, finalizedHint } = args
  const engine = useRecordingEngine({ onFallbackCommit })
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  // 転写経路 (log/207). 全 surface の録音開始が本 hook の startRecording を通るため、
  // 経路の注入点はここに 1 つだけ置く (page ごとの分岐を作らない)。
  const { data: setting } = useSettingQuery()
  const transcriptionRoute = resolveTranscriptionRoute(
    setting?.transcription_mode
  )

  // 録音 hardware が動き出した時刻. stopRecording() の短録音判定 (10 秒以下 toast) に使う.
  // engine.state 基準 (= 'recording' | 'fallback' が hardware 録音中) で立て、 idle で消す.
  const recordingStartedAtRef = useRef<number | null>(null)
  useEffect(() => {
    if (engine.state === 'recording' || engine.state === 'fallback') {
      if (recordingStartedAtRef.current === null) {
        recordingStartedAtRef.current = Date.now()
      }
      return
    }
    if (engine.state === 'idle' || engine.state === 'starting') {
      recordingStartedAtRef.current = null
    }
  }, [engine.state])

  // engine の action 群は個別に useCallback 安定だが、 return object 自体は毎 render
  // 新 literal のため、 deps は使用する action だけに絞って wrapper の identity を安定させる.
  const {
    start: engineStart,
    stop: engineStop,
    pause: enginePause,
    resume: engineResume,
    cancel: engineCancel,
    adoptPausedSession,
  } = engine

  // 一時停止セッションの復元 (log/185 サーバ復元): event 詳細 serializer の
  // paused_streaming_session (= サーバ側 PAUSED SS + StreamingSegment から導出) を正本に
  // engine へ paused を復元する. sessionStorage 非依存のため、 患者切替・reload・
  // ブラウザ再起動・別端末を全て跨げる. サーバ状態由来なので消滅済み session の再 adopt も
  // 構造的に起きない.
  const adoptTargetEventId = event?.id ?? null
  const pausedInfo = event?.paused_streaming_session ?? null
  const engineState = engine.state
  useEffect(() => {
    if (!adoptTargetEventId || !pausedInfo) return
    if (engineState !== 'idle') return
    // log/185 検証 F7: adopt は「単一 event の paused セッション × 単一 event ページ」の
    // 厳密一致のみ. まとめて録音 (yakureki_ids 複数) を単一ページが部分一致で adopt すると
    // UI (event 1 件) と実 scope (N 件) が乖離したまま再開/確定してしまう. group の復元は
    // group ページ側の将来対応とし、 再開始の誤操作は back の per-event PAUSED 衝突 (400)
    // が明示メッセージで防ぐ.
    if (
      pausedInfo.yakureki_ids.length !== 1 ||
      pausedInfo.yakureki_ids[0] !== adoptTargetEventId
    ) {
      return
    }
    adoptPausedSession({
      sessionId: pausedInfo.session_id,
      eventIds: pausedInfo.yakureki_ids,
      nextSegmentIndex: pausedInfo.next_segment_index,
      stagedDurationS: pausedInfo.staged_duration_s,
    })
  }, [adoptTargetEventId, pausedInfo, engineState, adoptPausedSession])

  // 開始の唯一の入口 (= stopRecording と対. engine.start() 直呼びしない).
  // engine.start() は開始失敗 (マイク許可拒否 / session 作成失敗など) で false を返す.
  // 旧救済 blob を保持していた場合、 engine は失敗前の failed に丸ごと巻き戻っており、
  // この画面が非当事者なら見た目は idle のまま (= 無言で何も起きない) になるため、
  // toast で開始失敗を必ず通知する.
  // 収音制約の上書き (log/207 F. admin 専用). 経路と同じく設定由来なので注入点をここに揃える.
  const captureNoiseSuppression = setting?.capture_noise_suppression
  const captureAutoGainControl = setting?.capture_auto_gain_control
  const startRecording = useCallback(
    (params: RecordingStartParams) => {
      void engineStart({
        ...params,
        route: resolveStartRoute({
          requested: params.route,
          isFreeRecording: params.isFreeRecording,
          transcriptionMode: setting?.transcription_mode,
        }),
        captureOverrides: {
          noiseSuppression: captureNoiseSuppression,
          autoGainControl: captureAutoGainControl,
        },
      }).then((started) => {
        if (!started) {
          showToast(
            '録音を開始できませんでした。マイクの許可設定を確認してください',
            'error'
          )
        }
      })
    },
    [
      engineStart,
      showToast,
      setting?.transcription_mode,
      captureNoiseSuppression,
      captureAutoGainControl,
    ]
  )

  // 停止の唯一の入口 (= 全 surface はこれを呼ぶ. engine.stop() 直呼びしない).
  // 10 秒以下 (= back audio_policy の RECORDING_MIN_S、 upload 経路で 400 拒否) で
  // 停止した場合はその場で toast を出す (= 事前の常設ガード UI は置かず、 操作時に
  // レスポンシブに知らせる). 停止自体は止めない: streaming 経路なら Pass 1 transcript
  // で SOAP 生成が走るし、 blob 救済導線も従来どおり機能させる.
  const stopRecording = useCallback(() => {
    const startedAt = recordingStartedAtRef.current
    if (
      startedAt !== null &&
      (Date.now() - startedAt) / 1000 <= RECORDING_MIN_S
    ) {
      showToast(
        `録音が${RECORDING_MIN_S}秒以下のため、文字起こしできない場合があります`,
        'warning'
      )
    }
    void engineStop()
  }, [engineStop, showToast])

  // 一時停止の唯一の入口 (log/185). 失敗時は engine 側で retry 救済 (pause 再送) に
  // 倒れているため、 ここでは通知だけを担う.
  const pauseRecording = useCallback(() => {
    void enginePause().then((result) => {
      // 'noop' (guard 拒否 = 二重押下) は失敗ではないので通知しない (検証 B7).
      if (result === 'failed') {
        showToast(
          '一時停止できませんでした。通信環境を確認してください',
          'error'
        )
      }
    })
  }, [enginePause, showToast])

  // 再開の唯一の入口. 一時失敗は paused に留まり再操作可能、 サーバ session 消滅 (expired)
  // は engine がエントリを破棄して idle に戻すため、 それぞれ文言を分けて通知する.
  const resumeRecording = useCallback(() => {
    void engineResume().then((result) => {
      if (result === 'failed') {
        showToast(
          '録音を再開できませんでした。通信環境を確認して再度お試しください',
          'error'
        )
      } else if (result === 'expired') {
        showToast(
          '一時停止セッションの有効期限が切れたため破棄されました。お手数ですが再度録音してください',
          'error'
        )
      }
    })
  }, [engineResume, showToast])

  // 破棄の唯一の入口. surface は録音開始の後から終端までの間 (録音中 = β / settled paused) で、
  // **経路の健全性 (streaming 健在 / 低速 fallback / batch) は条件にしない** (ADR-0039).
  // どちらも「SOAP を作らず捨てる」で back への通知は同一のため入口も 1 本 (状態で分岐しない).
  // 「中断して移動」 (useActiveRecordingEmit) もここを通る.
  //
  // 順序が肝: engine.cancel() は mic 解放と idle 復帰を**同期**で済ませるため、 cache に
  // 'recording' が残ったままだと deriveViewStatus が engine=idle × event=recording を
  // finalize 直後とみなして 'generating' (「SOAP 生成中…」) に倒す. await しても窓が
  // 短くなるだけで消えないので、 **先に cache へ 'pending' を書いてから** サーバの
  // commit を待ち、 その後に真値を取り直す. 楽観更新の前に進行中の poll を止めないと、
  // 飛んでいた応答が後から着地して 'recording' で楽観値を巻き戻す (useSoapWrite と同じ作法).
  const discardRecording = useCallback(async () => {
    // engineCancel() が同期で空にするので先に確保する.
    const discardedIds = engine.recordingEventIds
    for (const id of discardedIds) {
      await queryClient.cancelQueries({ queryKey: yakurekiPollKey(id) })
      // paused の復元情報も消す. 残すと adopt effect が消えた session を拾いに行く.
      queryClient.setQueryData<YakurekiDetail>(yakurekiPollKey(id), (prev) =>
        prev
          ? { ...prev, status: 'pending', paused_streaming_session: null }
          : prev
      )
    }
    // engine は同期で idle に戻る (mic 解放). toast はその時点の事実 (この画面の録音は終わった)
    // を伝えるもので、 サーバの応答を待たせない. 通知の失敗は engine 側で握り潰し済み.
    const committed = engineCancel()
    showToast('録音を破棄しました', 'success')
    // back は event の PENDING 復帰とフリー録音 event の削除をこのリクエストで確定させる
    // (ADR-0039). 待たずに invalidate すると stale な 'recording' が楽観値を上書きする.
    const { deletedEventIds } = await committed
    // back が消したフリー録音は一覧の永続キャッシュ (delta 同期) から名指しで外す. delta は
    // updated_at 起点で hard delete を表現できず、 invalidate だけでは無名の薬歴が一覧に残る.
    removeYakurekiFromLocalCache(deletedEventIds)
    // 録音の終端では一覧を全件取り直す (停止の completed 遷移と同じ作法). 他端末の破棄で消えた
    // 薬歴 (幽霊) は名指しでは外せないので、 この端末が録音を終えたときに全件で追いつく.
    // force_refresh は必須: 一覧 API はユーザー単位 10 秒キャッシュで、 素で取り直すと消した
    // ばかりの器を含む古い一覧を書き戻して幽霊を自分で作る. 失敗しても止めない (delta が続く).
    await executeSuperReload({ forceRefresh: true }).catch(() => undefined)
    // 一覧を先に取り直し切る (inactive な landing の snapshot も含める). フリー録音を破棄した
    // 詳細画面は下の 404 で '/' へ戻るが、 landing が古い snapshot の先頭 (= 消した event)
    // へ送り返すと 404 と '/' を往復する. 一覧が新しくなってから event を取り直す.
    await queryClient.invalidateQueries({
      queryKey: ['yakurekiList'],
      refetchType: 'all',
    })
    // 真値を取り直す. フリー録音は event ごと消えているので 404 が返り、
    // usePollEvent が終端扱いにして polling を止める (isGone).
    queryClient.invalidateQueries({ queryKey: ['yakureki'] })
  }, [engine.recordingEventIds, engineCancel, queryClient, showToast])

  // finalize ack 直後の polling lag を最小化 (= Option B / 完全本質).
  // back の finalize endpoint は ack 前に event.status='processing' に同期書込しているため、
  // ack 受領時点で cache (= 'recording') は stale. engine='completed' 遷移瞬間に invalidate を
  // emit して polling 5s 待たずに最新値を取得 → viewStatus='generating' に滑らかに移行.
  // (= 直接の transient window 表示は deriveViewStatus 側 (Option A) で 'generating' に倒す
  // 防御層も併存. 2 層構造で構造的に堅牢)
  //
  // 録音開始側も同じ理由で invalidate する. L1 (usePollEvent) は「サーバ側で何かが
  // 進行中」の間だけ polling する設計で、 pending (未録音) では回さない. 録音開始は
  // client 起点なので、 誰かが cache を突かないと status='recording' を観測できず
  // polling が起きない (= ハイライトが録音中ずっと出ない). back は stream/start と
  // 同一トランザクションで status=RECORDING を書くため、 start 応答後のこの invalidate
  // が観測する値は必ず recording 以降になる.
  //
  // 停止 (completed) では一覧を全件取り直す (破棄と同じ作法). delta 同期は削除を運べない
  // ので、 他端末の破棄で消えた薬歴 (幽霊) はこの端末が録音を終えたときに全件で追いつく.
  // force_refresh で back の 10 秒キャッシュを迂回し、 失敗しても delta の取り直しに倒す.
  const prevEngineStateRef = useRef<RecordingEngineState | null>(null)
  useEffect(() => {
    const prev = prevEngineStateRef.current
    if (prev !== 'completed' && engine.state === 'completed') {
      queryClient.invalidateQueries({ queryKey: ['yakureki'] })
      void executeSuperReload({ forceRefresh: true })
        .catch(() => undefined)
        .then(() =>
          queryClient.invalidateQueries({ queryKey: ['yakurekiList'] })
        )
    }
    if (prev !== 'recording' && engine.state === 'recording') {
      queryClient.invalidateQueries({ queryKey: ['yakureki'] })
      queryClient.invalidateQueries({ queryKey: ['yakurekiList'] })
    }
    prevEngineStateRef.current = engine.state
  }, [engine.state, queryClient])

  // 遷移で運ばれた hint (intendedBatch / finalized) はこの page で新しい録音が一度でも
  // 始まったら (= engine が idle を離れたら) stale とみなして恒久無効化する (index.tsx の
  // slowHintInvalidated と同じ理由. render 中 setState は "Adjusting state when props change").
  const [hintInvalidated, setHintInvalidated] = useState(false)
  if (
    !hintInvalidated &&
    (intendedBatchHint === true || finalizedHint === true) &&
    engine.state !== 'idle'
  ) {
    setHintInvalidated(true)
  }

  // finalize の証拠 (ADR-0041): この hook instance が engine='completed' を観測したか.
  // derive は「idle × event.status='recording'」を証拠があるときだけ generating と読む
  // (finalize ack 後、 サーバが processing を書く前の 1 poll 窓). 証拠は次の録音が始まったら
  // (starting / recording / fallback / paused 系) 消し、 event 切替でも消す. 破棄 (recording →
  // idle) は completed を経ないので立たない = 破棄直後に生成中を装わない.
  const eventId = event?.id ?? null
  const [engineStateSeen, setEngineStateSeen] = useState<RecordingEngineState>(
    engine.state
  )
  const [finalizedLocally, setFinalizedLocally] = useState(false)
  if (engineStateSeen !== engine.state) {
    setEngineStateSeen(engine.state)
    if (engine.state === 'completed') {
      setFinalizedLocally(true)
    } else if (engine.state !== 'idle' && engine.state !== 'failed') {
      setFinalizedLocally(false)
    }
  }
  const hasFinalizeEvidence =
    finalizedLocally || (finalizedHint === true && !hintInvalidated)

  // derive (= pure)
  const derived = useMemo(
    () =>
      deriveViewStatus({
        engineState: engine.state,
        event,
        engineEventIds: engine.recordingEventIds,
        engineError: engine.error,
        hasPendingBlob: engine.hasPendingBlob,
        isOffline: engine.isOffline,
        isIntendedBatch: engine.isIntendedBatch,
        hasFinalizeEvidence,
      }),
    [
      engine.state,
      engine.recordingEventIds,
      engine.error,
      engine.hasPendingBlob,
      engine.isOffline,
      engine.isIntendedBatch,
      event,
      hasFinalizeEvidence,
    ]
  )

  // transition guard: completed 一度達したら generating に戻さない.
  // event id 切替で reset. React 公式の "Adjusting state when props change" pattern
  // で render 中 setState (= 同 commit 内で即反映, lint OK).
  const [lastEventId, setLastEventId] = useState<string | null>(eventId)
  const [everCompleted, setEverCompleted] = useState(false)
  if (lastEventId !== eventId) {
    setLastEventId(eventId)
    setEverCompleted(false)
    setFinalizedLocally(false)
  }
  // 同一 event での再録音 (= 録音削除 → 再録音) で engine が動き出したら ratchet を解除する.
  // 解除しないと前回完了時の everCompleted が残り、 停止後の 'generating' が 'completed' に
  // マスクされて Post 画面へ早期 flip する (= 生成中 checklist / 低速バナーが出ないまま
  // SOAP 未確定の Post 表示になる). ratchet 本来の目的 (SOAP 再生成中の Post 維持) は
  // regen では engine が動かないため影響しない.
  if (
    everCompleted &&
    (engine.state === 'starting' ||
      engine.state === 'recording' ||
      engine.state === 'fallback')
  ) {
    setEverCompleted(false)
  }
  if (derived.status === 'completed' && !everCompleted) {
    setEverCompleted(true)
  }

  // ratchet 適用: generating ↔ completed の flicker を抑え、 一度 completed を見たら以後維持.
  const status: ViewStatus =
    derived.status === 'generating' && everCompleted
      ? 'completed'
      : derived.status

  // justCompleted (= status='completed' に切り替わった瞬間 1 render だけ true)
  const [prevStatus, setPrevStatus] = useState<ViewStatus | null>(null)
  const justCompleted = status === 'completed' && prevStatus !== 'completed'
  if (prevStatus !== status) {
    setPrevStatus(status)
  }

  // ratchet 後 status を反映した最終 derive (= status 変わった分だけ checklist 等を再計算)
  const checklistVisible =
    status === 'stopping' || status === 'generating' || status === 'completed'
  const rawChecklistStep: ChecklistStep =
    status === 'completed' ? 4 : derived.checklistStep

  // ─── UX smoothing: 各 step 遷移に jitter 付き min dwell を入れる ─────────
  // back の transcription.status が高速で完了すると raw step が 0→3 へ瞬時に飛ぶため、
  // 演出として 1 step ずつ 1.0-2.0s (random jitter) で進める. ground truth (= raw) は
  // upper bound として尊重 (= raw を越えて進めない / raw 下がっても regression しない).
  //
  // step 4 (= 生成完了) は status='completed' でのみ到達. smoothed が 4 に達したとき
  // allDone=true を返し、 ProcessingChecklist が全 step check 表示に切替.
  // step / enteredAt / dwell は 1 state にまとめて atomic 更新 + useState lazy init で
  // Date.now / Math.random を render 中の impure call にしない.
  // dwell の経路分岐は「この録音がどの経路か」= engine.isIntendedBatch を見る
  // (現在の設定 transcriptionRoute ではない: 設定は生成中にも変わりうる).
  // 別 page で録音が完了して遷移してきた場合は engine が新 instance のため、 caller 注入の
  // hint で補う (hint の失効は上の hintInvalidated).
  const dwellIsBatch =
    engine.isIntendedBatch || (intendedBatchHint === true && !hintInvalidated)
  const [smoothing, setSmoothing] = useState<{
    step: ChecklistStep
    enteredAt: number
    dwell: number
  }>(() => ({
    step: 0,
    enteredAt: Date.now(),
    // mount 直後の初回 dwell も経路を見る (hint 付きで /soap に遷移してきた直後の
    // step 0→1 だけ streaming 定数で進んでしまわないように)
    dwell: sampleStepDwellMs(dwellIsBatch),
  }))

  // recording 開始 / idle に戻った段階で step を 0 に reset (= 次セッション準備).
  // setTimeout(0) で commit 完了後に asynchronously setState することで
  // react-hooks/set-state-in-effect 規約 + 「同期 setState in effect」 を回避.
  useEffect(() => {
    if (status !== 'idle' && status !== 'starting' && status !== 'recording') {
      return
    }
    const t = window.setTimeout(() => {
      setSmoothing({
        step: 0,
        enteredAt: Date.now(),
        dwell: sampleStepDwellMs(dwellIsBatch),
      })
    }, 0)
    return () => window.clearTimeout(t)
  }, [status, dwellIsBatch])

  // raw が smoothed より先行している間、 jitter dwell で 1 step ずつ追従.
  useEffect(() => {
    if (rawChecklistStep <= smoothing.step) return
    const elapsed = Date.now() - smoothing.enteredAt
    const remaining = Math.max(0, smoothing.dwell - elapsed)
    const t = window.setTimeout(() => {
      setSmoothing((prev) => ({
        step: Math.min(rawChecklistStep, prev.step + 1) as ChecklistStep,
        enteredAt: Date.now(),
        dwell: sampleStepDwellMs(dwellIsBatch),
      }))
    }, remaining)
    return () => window.clearTimeout(t)
  }, [rawChecklistStep, smoothing, dwellIsBatch])

  // status='completed' (= 全工程完了) なら smoothing の dwell を待たず即 allDone にする.
  // 生成中は smoothing.step が段階表示を駆動するが、 完了後にまで演出 dwell を残すと
  // 「サーバ側は SOAP 完成済なのに UI が生成中のまま」 という体感 lag になるため上書きする.
  const checklistStep: ChecklistStep =
    status === 'completed' ? 4 : smoothing.step
  const checklistAllDone = status === 'completed'
  const showStopCta = status === 'recording'
  const showRecordingCta = status === 'idle'
  // paused は nav block しない (= 一時停止中に別患者の録音が可能. log/185).
  // pausing/resuming の in-flight 中だけは blob/通信が宙にあるため block する.
  const isNavBlocked =
    status === 'starting' ||
    status === 'recording' ||
    status === 'stopping' ||
    engine.state === 'pausing' ||
    engine.state === 'resuming'
  const ctaLabel =
    status === 'recording'
      ? '録音中'
      : status === 'paused'
      ? '一時停止中'
      : status === 'stopping'
      ? '処理中'
      : status === 'generating'
      ? 'SOAP 生成中'
      : '録音開始'

  return {
    status,
    transcriptionRoute,
    connectionStatus: derived.connectionStatus,
    isMicActive: derived.isMicActive,
    isNavBlocked,
    showRecordingCta,
    showStopCta,
    showPauseCta: derived.showPauseCta,
    showResumeCta: derived.showResumeCta,
    ctaLabel,
    checklistVisible,
    checklistStep,
    checklistAllDone,
    errorBannerKind: derived.errorBannerKind,
    showRetryCta: derived.showRetryCta,
    errorMessage: derived.errorMessage,
    isRetryHopeless: derived.isRetryHopeless,
    justCompleted,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    discardRecording,
    engine,
    event,
  }
}
